/**
 * deriveChargerDecision — the single source of truth for the
 * EvProfile status badge AND the click-time verification verdict.
 *
 * ── PRODUCT RULE this helper encodes ─────────────────────────
 *
 *   The primary CTA on the charger profile is INVARIANT:
 *     • The button always reads "Get Started".
 *     • The button is always enabled (except momentarily during
 *       an in-flight click-time re-verify, which is EvProfile's
 *       concern — not this helper's).
 *     • Authoritative validation happens ON TAP, against a fresh
 *       fetch, using this helper's `canAttemptStart` verdict.
 *
 *   The badge remains responsible for presenting the operational
 *   status ("Ready to Charge" / "Charging" / "Reserved" /
 *   "Under Maintenance" / "Unavailable" / "Availability
 *   unverified" / …). The CTA label MUST NOT mirror the badge.
 *
 *   This helper NEVER returns a status-derived CTA label or
 *   disabled flag. That was the previous shape (ctaLabel /
 *   ctaEnabled) and produced the exact "Ready to Charge" +
 *   disabled "Unavailable" button contradiction we're fixing.
 *
 * ── Inputs ───────────────────────────────────────────────────
 *
 *   live               — sessionContext.state.live (may be {})
 *   device             — fetched station-data payload (or null)
 *   deviceFetchFailed  — boolean: fetch threw / non-ok / timed out
 *
 * ── Output ───────────────────────────────────────────────────
 *
 *   {
 *     kind,              // 'ready' | 'in-use' | 'unavailable' |
 *                        // 'faulted' | 'reserved' | 'maintenance' |
 *                        // 'unverified' | 'legacy-fallback'
 *     status,            // fed to <StatusPill status=… />
 *     badgeLabel,        // resolved display label (matches StatusPill)
 *     canAttemptStart,   // boolean — click-time verdict; NOT CTA state
 *     blockReason,       // string | null — toast shown when
 *                        //   canAttemptStart is false and the user taps
 *   }
 *
 *   Contracts:
 *     • When `derived.chargeable === true`, canAttemptStart = true
 *       and blockReason = null.
 *     • When the fetch failed (deviceFetchFailed), the badge says
 *       "Availability unverified" and canAttemptStart = false —
 *       the click flow will re-fetch fresh state and re-derive.
 *     • Every non-ready kind carries a specific `blockReason` string
 *       matching the product spec ("This charger is currently under
 *       maintenance.", "This charger is already in use.", etc.). The
 *       string is the ONLY user-facing text for a blocked charger.
 *     • Live SSE transaction wins over the fetched status (in-use).
 */

import { resolveBadgeLabel } from '../../Common/StatusPill/StatusPill.jsx';

const IN_USE_STATES = new Set([
    'Preparing', 'Charging', 'SuspendedEV', 'SuspendedEVSE',
    'Finishing', 'Occupied',
]);
const UNAVAILABLE_STATES = new Set([
    'Unavailable', 'Disabled', 'Busy',
]);

// User-facing block reasons — surfaced as a toast when the fresh
// click-time decision says canAttemptStart is false. The strings
// are the product-spec wording; do not paraphrase without updating
// the spec.
const BLOCK_REASONS = Object.freeze({
    maintenance:  'This charger is currently under maintenance.',
    faulted:      'This charger is currently unavailable due to a fault.',
    reserved:     'This charger is currently reserved.',
    in_use:       'This charger is already in use.',
    offline:      "We can't reach this charger right now. Please try again shortly.",
    unverified:   "We couldn't confirm this charger's availability. Please try again.",
    unavailable:  'This charger is not ready to start a session right now.',
    unknown:      'This charger is not ready to start a session right now.',
});

function blockedReadyResult(kind, status, blockReason) {
    return {
        kind,
        status,
        badgeLabel: resolveBadgeLabel(status),
        canAttemptStart: false,
        blockReason,
    };
}

export function deriveChargerDecision({
    live = {},
    device = null,
    deviceFetchFailed = false,
} = {}) {
    // ── 1. Live SSE transaction wins ─────────────────────────
    // If we hold a live tx_id the customer is already in a
    // session on this connector — treat as in-use and let the
    // downstream steps route them to the active-session UI.
    const hasActiveTransaction = live?.tx_id != null;
    if (hasActiveTransaction) {
        const liveStatus = live.status || 'Charging';
        return blockedReadyResult('in-use', liveStatus, BLOCK_REASONS.in_use);
    }

    // ── 2. Verification failed ───────────────────────────────
    // The detailed availability endpoint is our only source of
    // truth for "can we start". If it failed we deliberately
    // hide the raw fallback status ('Available' from the public
    // list) and label the badge honestly. canAttemptStart=false
    // — the click flow will re-fetch fresh state at tap time.
    if (deviceFetchFailed) {
        return blockedReadyResult('unverified', 'Unverified', BLOCK_REASONS.unverified);
    }

    // ── 3. Backend `derived` block — canonical ───────────────
    const derived = device?.derived || null;
    if (derived) {
        // Preserve the underlying operational state in the badge
        // (e.g. Available under maintenance still SHOWS Available)
        // but let `chargeable` gate `canAttemptStart` independently.
        // Matches admin behaviour: users see the true state, the
        // system prevents starts based on the full check.
        const status = derived.displayStatus || 'Available';

        if (derived.chargeable === true) {
            return {
                kind: 'ready',
                status,
                badgeLabel: resolveBadgeLabel(status),
                canAttemptStart: true,
                blockReason: null,
            };
        }

        // Not chargeable — figure out why so the click-time toast
        // speaks plainly. Maintenance race first (rare — verified
        // path usually catches this earlier), then explicit blocked
        // states, then generic Unavailable.
        if (derived.maintenanceMode === true) {
            return blockedReadyResult('maintenance', status, BLOCK_REASONS.maintenance);
        }
        if (status === 'Faulted') {
            return blockedReadyResult('faulted', status, BLOCK_REASONS.faulted);
        }
        if (status === 'Reserved') {
            return blockedReadyResult('reserved', status, BLOCK_REASONS.reserved);
        }
        if (IN_USE_STATES.has(status)) {
            return blockedReadyResult('in-use', status, BLOCK_REASONS.in_use);
        }
        return blockedReadyResult('unavailable', status, BLOCK_REASONS.unavailable);
    }

    // ── 4. Legacy fallback ───────────────────────────────────
    // Older API responses lack the `derived` block. Fall back
    // to station_status[0].status; the click flow re-fetches
    // fresh data anyway. Rolling deploys where API and web tiers
    // upgrade separately still work.
    const apiStatus = device?.station_status?.[0]?.status;
    const hasActiveSessionOnServer = device?.activeSession != null;
    const status = apiStatus
        || (hasActiveSessionOnServer ? 'Charging' : null)
        || 'Available';

    if (status === 'Faulted') {
        return blockedReadyResult('legacy-fallback', status, BLOCK_REASONS.faulted);
    }
    if (status === 'Reserved') {
        return blockedReadyResult('legacy-fallback', status, BLOCK_REASONS.reserved);
    }
    if (IN_USE_STATES.has(status)) {
        return blockedReadyResult('legacy-fallback', status, BLOCK_REASONS.in_use);
    }
    if (UNAVAILABLE_STATES.has(status)) {
        return blockedReadyResult('legacy-fallback', status, BLOCK_REASONS.unavailable);
    }
    // Available (or unrecognised → treated as Available for the
    // legacy path only, matching the pre-derived behaviour). The
    // click flow will re-fetch and re-derive against fresh data.
    return {
        kind: 'legacy-fallback',
        status,
        badgeLabel: resolveBadgeLabel(status),
        canAttemptStart: true,
        blockReason: null,
    };
}

// Exported for tests + consumers that want to reuse the reason
// dictionary (e.g. instrumentation, alternate surfaces).
export const CHARGER_BLOCK_REASONS = BLOCK_REASONS;
export const __internals = { IN_USE_STATES, UNAVAILABLE_STATES };
